/**
 * Page de gestion des templates d'emails personnalisés
 */

import { useState } from "react";
import { useEmailTemplates } from "@/lib/useEmailTemplates";
import { EmailTemplate } from "@/types/emailTemplates";
import { TemplateCard } from "@/components/templates/TemplateCard";
import { TemplateModal } from "@/components/templates/TemplateModal";
import { QuoteReminderTemplatesPanel } from "@/components/templates/QuoteReminderTemplatesPanel";
import { useToast } from "@/lib/useToast";
import { Toast } from "@/components/ui";

type TemplateTab = "commercial" | "reminders";

export default function TemplatesPage() {
  const { templates, loading, error, createTemplate, updateTemplate, deleteTemplate } = useEmailTemplates();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [activeTab, setActiveTab] = useState<TemplateTab>("commercial");
  const { toast, showToast, hideToast } = useToast();

  const handleOpenCreateModal = () => {
    setEditingTemplate(null);
    setModalOpen(true);
  };

  const handleOpenEditModal = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingTemplate(null);
  };

  const handleSave = async (name: string, body: string, color: string) => {
    if (editingTemplate) {
      // Mode édition
      const result = await updateTemplate(editingTemplate.id, { name, body, color });
      if (result.success) {
        showToast({ message: "Template modifié avec succès !", type: "success" });
      } else {
        showToast({ message: `Erreur : ${result.error}`, type: "error" });
        throw new Error(result.error);
      }
    } else {
      // Mode création
      const result = await createTemplate(name, body, color);
      if (result.success) {
        showToast({ message: "Template créé avec succès !", type: "success" });
      } else {
        showToast({ message: `Erreur : ${result.error}`, type: "error" });
        throw new Error(result.error);
      }
    }
  };

  const handleDelete = async (id: string) => {
    const result = await deleteTemplate(id);
    if (result.success) {
      showToast({ message: "Template supprimé !", type: "success" });
    } else {
      showToast({ message: `Erreur : ${result.error}`, type: "error" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Templates</h1>
              <p className="mt-1 text-sm text-gray-600">
                Configurez les messages commerciaux et les relances automatiques.
              </p>
            </div>
            {activeTab === "commercial" && (
              <button
                onClick={handleOpenCreateModal}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Créer un template
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActiveTab("commercial")}
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              activeTab === "commercial"
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            Templates commerciaux
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("reminders")}
            className={`rounded-md px-3 py-2 text-sm font-medium ${
              activeTab === "reminders"
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-700 border border-gray-200 hover:bg-gray-50"
            }`}
          >
            Relances devis
          </button>
        </div>

        {activeTab === "reminders" && <QuoteReminderTemplatesPanel />}

        {activeTab === "commercial" && (
          <>
        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <p className="mt-2 text-sm text-gray-600">Chargement des templates...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Erreur de chargement
                </h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && templates.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun template</h3>
            <p className="mt-1 text-sm text-gray-500">
              Créez votre premier template pour commencer
            </p>
            <div className="mt-6">
              <button
                onClick={handleOpenCreateModal}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Créer mon premier template
              </button>
            </div>
          </div>
        )}

        {/* Grid de templates */}
        {!loading && !error && templates.length > 0 && (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {templates.length} template{templates.length > 1 ? "s" : ""}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onEdit={handleOpenEditModal}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </>
        )}
          </>
        )}
      </div>

      {/* Modal */}
      <TemplateModal
        isOpen={modalOpen}
        template={editingTemplate}
        onClose={handleCloseModal}
        onSave={handleSave}
      />
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
      />
    </div>
  );
}
